import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { getAdConfig, createPost } from '../lib/api';
import { AdBanner } from '../components/AdBanner';
import { 
  ChevronLeft, MapPin, Clock, Flame, Trash2, 
  Loader2, Footprints, Calendar, AlertTriangle, X, Share2, Zap
} from 'lucide-react';
import RouteMap from '../components/RouteMap';
import { inferActivityType, getActivityLabel } from '../lib/activity';

import { API_URL } from '../config/api';

export default function MieiAllenamenti() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { convertDistance, distanceUnit } = useUnits();
  const showWorkoutMap = localStorage.getItem('slimmy_workout_map') !== 'false';
  
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [shareModal, setShareModal] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [adConfig, setAdConfig] = useState({ workouts_every_n_items: 3 });

  useEffect(() => {
    // scroll restoration handled by App.js
    fetchWorkouts();
    getAdConfig().then(res => setAdConfig(res.data)).catch(() => {});
  }, []);

  const fetchWorkouts = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/workouts`, {
        credentials: 'include',
        headers: sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setWorkouts(data);
      }
    } catch (err) {
      console.error('Error fetching workouts:', err);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    
    setDeleting(deleteModal.workout_id);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/workouts/${deleteModal.workout_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}
      });
      if (res.ok) {
        setWorkouts(prev => prev.filter(w => w.workout_id !== deleteModal.workout_id));
      }
    } catch (err) {
      console.error('Error deleting workout:', err);
    } finally {
      setDeleting(null);
      setDeleteModal(null);
    }
  };

  const handleShare = async () => {
    if (!shareModal) return;
    setSharing(true);
    try {
      const distanceStr = convertDistance(shareModal.distance_km || 0).toFixed(2);
      const durationStr = formatDuration(shareModal.duration_seconds || 0);
      const activityType = inferActivityType(shareModal.distance_km, shareModal.duration_seconds);
      const isRun = activityType === 'run';
      const fallbackText = isRun
        ? `Ho completato una corsa di ${distanceStr} ${distanceUnit} in ${durationStr}! 🏃‍♂️`
        : `Ho completato una camminata di ${distanceStr} ${distanceUnit} in ${durationStr}! 🚶‍♂️`;
      const sharedText = (
        isRun
          ? t('myWorkouts.shareTextRun', fallbackText)
          : t('myWorkouts.shareText', fallbackText)
      )
        .replace('{distance}', distanceStr)
        .replace('{unit}', distanceUnit)
        .replace('{duration}', durationStr);
      await createPost({
        post_type: 'camminata',
        text: sharedText,
        data: {
          distance: shareModal.distance_km || 0,
          duration_seconds: shareModal.duration_seconds || 0,
          calories: shareModal.calories || 0,
          positions: (shareModal.positions || []).slice(0, 100),
          activity_type: activityType,
        }
      });
      setShareModal(null);
      navigate('/app/feed');
    } catch (err) {
      console.error('Error sharing workout:', err);
    } finally {
      setSharing(false);
    }
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate totals
  const totalDistance = workouts.reduce((sum, w) => sum + (w.distance_km || 0), 0);
  const totalCalories = workouts.reduce((sum, w) => sum + (w.calories || 0), 0);
  const totalDuration = workouts.reduce((sum, w) => sum + (w.duration_seconds || 0), 0);

  return (
    <div className={`${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="miei-allenamenti-page">
      {/* Fixed Header - with safe area */}
      <div className={`fixed-header px-4 pb-4 ${isDark ? 'bg-[#080D1A]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button 
              onClick={() => navigate(-1)}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
              data-testid="btn-back"
            >
              <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
            </button>
            <div className="flex-1">
              <h1 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('dashboard.myWorkouts') || 'I miei allenamenti'}
              </h1>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {workouts.length} {t('dashboard.workoutsCount') || 'allenamenti'}
              </p>
            </div>
            <Footprints className="w-8 h-8 text-[#3B82F6]" />
          </div>

          {/* Stats Summary */}
          {workouts.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <MapPin className="w-4 h-4 mx-auto mb-1 text-[#16A34A]" />
                <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {convertDistance(totalDistance).toFixed(1)}
                </p>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{(t('dashboard.totalKm') || 'km totali').replace('km', distanceUnit)}</p>
              </div>
              <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <Flame className="w-4 h-4 mx-auto mb-1 text-[#E8334A]" />
                <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {totalCalories}
                </p>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('dashboard.caloriesBurned') || 'kcal bruciate'}</p>
              </div>
              <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <Clock className="w-4 h-4 mx-auto mb-1 text-[#3B82F6]" />
                <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatDuration(totalDuration)}
                </p>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('dashboard.totalTime') || 'tempo totale'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content - with padding top for fixed header */}
      <div className={`pb-24 p-4 ${workouts.length > 0 ? 'content-below-header-xl' : 'content-below-header'}`}>
        {/* Spacer for breathing room */}
        <div className="h-4"></div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : workouts.length === 0 ? (
          <div className={`text-center py-20 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            <Footprints className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">{t('dashboard.noWorkouts') || 'Nessun allenamento'}</p>
            <p className="text-sm">{t('dashboard.noWorkoutsDesc') || 'I tuoi allenamenti appariranno qui'}</p>
            <button
              onClick={() => navigate('/app/camminata')}
              className="mt-4 px-6 py-3 rounded-full bg-[#3B82F6] text-white font-medium"
            >
              {t('dashboard.startWalk') || 'Inizia camminata'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* First ad banner at the top */}
            <AdBanner position="workouts" className="rounded-2xl" />
            
            {workouts.map((workout, index) => (
              <React.Fragment key={workout.workout_id}>
                <div
                  className={`rounded-2xl overflow-hidden ${
                    isDark 
                      ? 'bg-white/5' 
                      : 'bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {(() => {
                          // Distinguish walk vs. run from avg speed and
                          // pick a matching colour + icon for the avatar.
                          const activity = inferActivityType(workout.distance_km, workout.duration_seconds);
                          const isRun = activity === 'run';
                          const accent = isRun ? '#F97316' : '#3B82F6';
                          const Icon = isRun ? Zap : Footprints;
                          return (
                            <div
                              className="w-12 h-12 rounded-xl flex items-center justify-center"
                              style={{ background: `${accent}${isDark ? '33' : '1A'}` }}
                            >
                              <Icon className="w-6 h-6" style={{ color: accent }} />
                            </div>
                          );
                        })()}
                        <div>
                          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getActivityLabel(workout.distance_km, workout.duration_seconds, t)}
                          </h3>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Calendar className={`w-3 h-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                              {formatDate(workout.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions: share + delete */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShareModal(workout)}
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isDark ? 'bg-white/5 hover:bg-[#3B82F6]/20' : 'bg-gray-100 hover:bg-blue-100'
                          } transition-colors`}
                          data-testid={`share-workout-btn-${workout.workout_id}`}
                          title={t('myWorkouts.share') || 'Condividi'}
                        >
                          <Share2 className="w-4 h-4 text-[#3B82F6]" />
                        </button>
                        <button
                          onClick={() => setDeleteModal(workout)}
                          disabled={deleting === workout.workout_id}
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isDark ? 'bg-white/5 hover:bg-red-500/20' : 'bg-gray-100 hover:bg-red-100'
                          } transition-colors`}
                        >
                          {deleting === workout.workout_id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                          ) : (
                            <Trash2 className="w-4 h-4 text-red-500" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#16A34A]" />
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {convertDistance(workout.distance_km || 0).toFixed(2)} {distanceUnit}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#3B82F6]" />
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatDuration(workout.duration_seconds || 0)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Flame className="w-4 h-4 text-[#E8334A]" />
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {workout.calories || 0} kcal
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Route map */}
                  {showWorkoutMap && workout.positions && workout.positions.length >= 2 && (
                    <RouteMap positions={workout.positions} height={160} className="mx-4 mb-4" />
                  )}
                </div>
                {/* Insert ad after every N workouts */}
                {(index + 1) % (adConfig.workouts_every_n_items || 3) === 0 && (
                  <AdBanner position="workouts" className="rounded-2xl" />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Share Workout Modal */}
      {shareModal && createPortal(
        <div className="fixed inset-0 z-[200]" data-testid="share-workout-modal">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !sharing && setShareModal(null)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}>
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
                  <Share2 className="w-7 h-7 text-[#3B82F6]" />
                </div>
              </div>

              <h3 className={`text-lg font-semibold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('myWorkouts.shareConfirm') || 'Pubblicare nel feed?'}
              </h3>
              <p className={`text-sm text-center mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('myWorkouts.shareDesc') || 'I tuoi amici vedranno questo allenamento nel feed con mappa, distanza e durata.'}
              </p>

              {/* Preview */}
              <div className={`rounded-xl p-3 mb-5 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-4 justify-center">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#16A34A]" />
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {(shareModal.distance_km || 0).toFixed(2)} km
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#3B82F6]" />
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {formatDuration(shareModal.duration_seconds || 0)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Flame className="w-4 h-4 text-[#E8334A]" />
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {shareModal.calories || 0} kcal
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShareModal(null)}
                  disabled={sharing}
                  className={`flex-1 py-3 rounded-full font-medium ${
                    isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {t('dashboard.cancel') || 'Annulla'}
                </button>
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  className="flex-1 py-3 rounded-full font-medium bg-[#3B82F6] text-white flex items-center justify-center gap-2"
                  data-testid="share-workout-confirm-btn"
                >
                  {sharing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      {t('myWorkouts.publish') || 'Pubblica'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && createPortal(
        <div className="fixed inset-0 z-[200]" data-testid="delete-workout-modal">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setDeleteModal(null)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}>
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-red-500" />
                </div>
              </div>
              
              <h3 className={`text-lg font-semibold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('dashboard.deleteWorkout') || 'Elimina allenamento?'}
              </h3>
              <p className={`text-sm text-center mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {(t('dashboard.deleteWorkoutDesc') || 'Stai per eliminare la camminata di {distance} {unit}. Questa azione non può essere annullata.').replace('{distance}', convertDistance(deleteModal.distance_km || 0).toFixed(2)).replace('{unit}', distanceUnit)}
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModal(null)}
                  className={`flex-1 py-3 rounded-full font-medium ${
                    isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {t('dashboard.cancel') || 'Annulla'}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 py-3 rounded-full font-medium bg-red-500 text-white flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      {t('dashboard.delete') || 'Elimina'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
